<template>
  <div class="[ new-activity-settings ] relative w-full">

    <!-- SLOT: Activity Type Selector -->
    <slot name="activity-selector"></slot>

    <!-- SLOT: Activity Title Input -->
    <slot name="title-input"></slot>

    <h3 class="mb-0 pt-4 text-xs lg:text-sm font-bold uppercase leading-snug select-none pointer-events-none">GPS Settings</h3>

    <!-- Tracking Allowed Status -->
    <div class="[ setting-row ] flex items-center justify-between py-2">
      <div class="[ setting-label ] flex items-center uppercase font-bold text-[10px] lg:text-xs select-none text-gray-600">
        <svg class="[ info-icon ] w-4 h-4 mr-1 cursor-pointer shrink-0" @click="$emit('show-settings-info')">
          <use xlink:href="#info" />
        </svg>
        Allow tracking:
      </div>
      <div class="[ setting-value-with-toggle ] flex items-center gap-2 text-sm lg:text-base font-semibold select-none">
        <toggle-button
          class="ml-auto"
          color="#059605"
          @change="$emit('toggle-tracking-allowed')"
          :value="trackingAllowed"
          :labels="true" />
      </div>
    </div>

    <!-- GPS Settings List -->
    <ul class="pb-2 lg:pb-4 text-xs lg:text-sm" :class="{ 'opacity-30': !trackingAllowed }">

      <!-- GPS Access -->
      <li class="flex flex-row items-center select-none w-full mb-1 text-xs leading-none sm:text-sm sm:leading-tight">
        <div class="[ settings-key ]">GPS Access:</div>
        <div class="[ settings-value-row ] shrink!">
          <div class="text-green-600 select-none pointer-events-none" v-if="geolocationPermissionStatus === 'granted'">{{ geolocationPermissionStatus }}</div>

          <svg
            class="w-5 h-5 ml-2 text-green-600 select-none cursor-pointer hover:text-green-800 focus:outline-none focus:[box-shadow:var(--fx-focus-ring-primary)]"
            @click="$emit('zoom-to-location')"
            v-if="geolocationPermissionStatus === 'granted' && enableHighAccuracy && trackingAllowed">
            <use xlink:href="#current-geolocation" />
          </svg>

          <div v-if="geolocationPermissionStatus !== 'granted'" class="relative flex flex-col text-3xs sm:text-[10px] mt-2 select-none">
            <button
              @click="$emit('request-permission')"
              v-if="geolocationPermissionStatus !== 'denied'"
              class="mb-2 bg-transparent border border-pink-800 text-pink-800 px-2 py-1 rounded-md transition hover:bg-pink-200"
              :disabled="!trackingAllowed">
              Grant GPS Access
            </button>

            <div class="mr-2 text-sm leading-tight text-pink-800 wrap-break-word" v-if="geolocationPermissionStatus === 'denied'">
              Geolocation Access has been denied, probably because this site is not on a secure domain (https).
            </div>
          </div>
        </div>
      </li>

      <!-- High Accuracy -->
      <li class="flex flex-row items-center select-none w-full mb-1 text-xs leading-none sm:text-sm sm:leading-tight">
        <div class="[ settings-key ]">High Accuracy:</div>
        <div class="grow flex items-center justify-between text-sm leading-none text-pink-800 sm:text-base sm:leading-tight">
          <span :class="{ 'text-green-600': enableHighAccuracy }">{{ enableHighAccuracyText }}</span>
          <toggle-button
            class="ml-auto"
            color="#059605"
            @change="$emit('toggle-high-accuracy')"
            :value="enableHighAccuracy"
            :disabled="!trackingAllowed"
            :labels="true" />
        </div>
      </li>

      <!-- Timeout -->
      <li class="[ timeout-row ] flex flex-row items-center select-none w-full mb-1 text-xs leading-none sm:text-sm sm:leading-tight">
        <div class="[ settings-key ]">Timeout:</div>
        <div class="grow flex items-center text-sm leading-none text-pink-800 sm:text-base sm:leading-tight">
          <span :class="{ 'text-green-600': timeout === 5000 }">{{ timeout }}ms</span>
          <span v-if="timeout === 5000" class="hidden md:inline-block text-3xs md:text-xs text-gray-500 ml-1">(standard)</span>
          <input
            type="range"
            class="[ timeout-slider ] ml-auto w-20 h-2 cursor-pointer appearance-none rounded-full bg-pink-200 sm:w-28 disabled:opacity-50 disabled:cursor-not-allowed"
            :value="timeout"
            min="1000"
            max="15000"
            step="500"
            :disabled="!trackingAllowed"
            @input="$emit('update-timeout', parseInt($event.target.value))"
          />
        </div>
      </li>

      <!-- Wake Lock -->
      <li class="flex flex-row items-center select-none w-full mb-1 text-xs leading-none sm:text-sm sm:leading-tight">
        <div class="[ settings-key ]">Screen wake-lock:</div>
        <div class="grow flex items-center justify-between text-sm leading-none text-pink-800 sm:text-base sm:leading-tight">
          <span :class="{ 'text-green-600': screenWakeLockEnabled }">{{ screenWakeLockText }}</span>
          <toggle-button
            class="ml-auto"
            color="#059605"
            @change="$emit('toggle-wake-lock')"
            :value="screenWakeLockEnabled"
            :disabled="!trackingAllowed"
            :labels="true" />
        </div>
      </li>

      <!-- Touch Screen Protection -->
      <li class="flex flex-row items-center select-none w-full mb-1 text-xs leading-none sm:text-sm sm:leading-tight">
        <div class="[ settings-key ]">
          <span class="sm:hidden">Protect Screen:</span>
          <span class="hidden sm:block">Touch Screen Protection:</span>
        </div>
        <div class="grow flex items-center justify-between text-sm leading-none text-pink-800 sm:text-base sm:leading-tight">
          <span :class="{ 'text-green-600': touchProtectionEnabled }">{{ touchProtectionText }}</span>
          <toggle-button
            class="ml-auto"
            color="#059605"
            @change="$emit('toggle-touch-protection')"
            :value="touchProtectionEnabled"
            :disabled="!trackingAllowed"
            :labels="true" />
        </div>
      </li>

      <!-- Activity Status -->
      <li class="flex flex-row items-center select-none w-full mb-1 text-xs leading-none sm:text-sm sm:leading-tight border-t border-pink-200 pt-3 mt-2 sm:mt-3">
        <div class="[ settings-key ]">Activity Status:</div>
        <div class="[ settings-value ]">{{ newActivityStatus }}</div>
      </li>

      <!-- Recording Status -->
      <li class="flex flex-row items-center select-none w-full mb-1 text-xs leading-none sm:text-sm sm:leading-tight">
        <div class="[ settings-key ]">Recording:</div>
        <div class="[ settings-value ]">{{ recording }}</div>
      </li>

    </ul>

  </div>
</template>

<script setup>
import { computed } from 'vue';

defineOptions({ name: 'ActivitySettingsPanel' });

const props = defineProps({
  newActivityStatus: {
    type: String,
    required: true
  },
  recording: {
    type: [Boolean, String],
    required: true
  },
  trackingAllowed: {
    type: Boolean,
    default: false
  },
  geolocationPermissionStatus: {
    type: String,
    default: 'prompt'
  },
  enableHighAccuracy: {
    type: Boolean,
    default: false
  },
  timeout: {
    type: Number,
    default: 5000
  },
  screenWakeLockEnabled: {
    type: Boolean,
    default: false
  },
  touchProtectionEnabled: {
    type: Boolean,
    default: false
  }
});

const enableHighAccuracyText = computed(() => (props.enableHighAccuracy ? 'enabled' : 'disabled'));
const screenWakeLockText = computed(() => (props.screenWakeLockEnabled ? 'enabled' : 'disabled'));
const touchProtectionText = computed(() => (props.touchProtectionEnabled ? 'enabled' : 'disabled'));
</script>

<style scoped>
@property --tw-leading {
  syntax: "*";
  inherits: false;
}

/* Pseudo-elements cannot be expressed in Tailwind */
.timeout-slider::-webkit-slider-thumb {
  appearance: none;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  cursor: pointer;
  background: rgb(var(--color-primary-800));
}

.timeout-slider::-moz-range-thumb {
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  cursor: pointer;
  border: 0;
  background: rgb(var(--color-primary-800));
}
</style>
